import type {
  Employee,
  PayrollDepartment,
  Location,
  Schedule,
  Position,
  PaymentFrequency,
  PayrollRunPreviewFiltersSnapshot,
  EmployeePayrollCalculationResultDto,
  PayrollRunPreviewDto,
  PayRecord,
} from '../types';
import type { FilterCondition, FilterFieldOption } from './employeeAdvancedFilters';
import {
  filterEmployeesWithAdvancedFilters,
  getStatusFilterFromFilters,
} from './employeeAdvancedFilters';
import type { PayrollReportLayout, PayrollReportFilters, PayrollLineBreakdown } from './payrollReportOutputCatalog';
import { defaultPayrollFilters, normalizePayrollLineBreakdown } from './payrollReportOutputCatalog';

export type PayrollBuilderFreqKind = 'weekly' | 'monthly' | 'fortnightly' | 'ooh-hourly';

export interface PayrollBuilderReportOptions {
  lineBreakdown: PayrollLineBreakdown;
  groupByEmployee: boolean;
  onlyWarnings: boolean;
  onlyErrors: boolean;
  lineCategories: string[];
}

export const DEFAULT_PAYROLL_BUILDER_REPORT_OPTIONS: PayrollBuilderReportOptions = {
  lineBreakdown: 'scheduleDayTypeBreakdown',
  groupByEmployee: false,
  onlyWarnings: false,
  onlyErrors: false,
  lineCategories: [],
};

export function parsePayrollBuilderReportOptions(json: string): PayrollBuilderReportOptions {
  try {
    const o = JSON.parse(json) as Partial<PayrollBuilderReportOptions>;
    return {
      lineBreakdown: normalizePayrollLineBreakdown(o.lineBreakdown),
      groupByEmployee: o.groupByEmployee === true,
      onlyWarnings: o.onlyWarnings === true,
      onlyErrors: o.onlyErrors === true,
      lineCategories: Array.isArray(o.lineCategories) ? o.lineCategories.map(String) : [],
    };
  } catch {
    return { ...DEFAULT_PAYROLL_BUILDER_REPORT_OPTIONS };
  }
}

export function buildPayrollReportFiltersFromOptions(
  runId: number | null,
  options: PayrollBuilderReportOptions
): PayrollReportFilters {
  return {
    payrollRunId: runId,
    onlyWarnings: options.onlyWarnings,
    onlyErrors: options.onlyErrors,
    lineCategories: options.lineCategories,
    lineBreakdown: options.lineBreakdown,
    groupByEmployee: options.groupByEmployee,
  };
}

function filterScalar<T>(value: string | number | boolean | number[] | string[] | undefined): T | null {
  if (value === '' || value == null) return null;
  if (Array.isArray(value)) return null;
  return value as T;
}

function filterIdArray(value: string | number | boolean | number[] | string[] | undefined): number[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  return value.map((v) => Number(v)).filter((n) => !Number.isNaN(n));
}

export function buildPayrollRunSnapshotFromEmployeeFilters(params: {
  employeeFilters: FilterCondition[];
  filterFields: FilterFieldOption[];
  freqKind: PayrollBuilderFreqKind;
  periodSelectionLabel: string | null;
  payrollDepartments: PayrollDepartment[];
  locations: Location[];
  schedules: Schedule[];
  positions: Position[];
  paymentFrequencies: PaymentFrequency[];
  freqIds: { weeklyId: number | null; monthlyId: number | null; fortnightlyId: number | null };
  allEmployees: Employee[];
  allPayRecords?: PayRecord[];
}): PayrollRunPreviewFiltersSnapshot {
  const {
    employeeFilters,
    filterFields,
    freqKind,
    periodSelectionLabel,
    payrollDepartments,
    locations,
    schedules,
    positions,
    paymentFrequencies,
    freqIds,
    allEmployees,
    allPayRecords = [],
  } = params;

  const findFilter = (field: string) => employeeFilters.find((f) => f.field === field);

  const deptVal = filterScalar<number>(findFilter('payrollDepartmentId')?.value);
  const locVal = filterScalar<number>(findFilter('mainLocationId')?.value);
  const scheduleMulti = filterIdArray(findFilter('scheduleIds')?.value);
  const scheduleSingle = filterScalar<number>(scheduleMulti?.length === 1 ? scheduleMulti[0] : undefined);
  const positionVal = filterScalar<number>(findFilter('mainPositionId')?.value);

  let paymentFrequencyId: number | null = null;
  if (freqKind === 'weekly' || freqKind === 'ooh-hourly') paymentFrequencyId = freqIds.weeklyId;
  else if (freqKind === 'monthly') paymentFrequencyId = freqIds.monthlyId;
  else paymentFrequencyId = freqIds.fortnightlyId;

  const matchedEmployees = filterEmployeesWithAdvancedFilters({
    allEmployees,
    selectedLocationId: null,
    statusFilter: getStatusFilterFromFilters(employeeFilters),
    filters: employeeFilters,
    searchTerm: '',
    filterFields,
    sortColumn: 'name',
    sortDirection: 'asc',
    allPayRecords,
    allLicenses: [],
    allFeedbackRecords: [],
    allTrainingRecords: [],
    allEmployeeNotes: [],
  });

  const allActiveCount = allEmployees.filter((e) => e.isActive).length;
  const employeeIds =
    matchedEmployees.length > 0 && matchedEmployees.length < allActiveCount
      ? matchedEmployees.map((e) => e.id)
      : null;

  return {
    freqKind,
    periodSelectionLabel,
    payrollDepartmentId: deptVal,
    payrollDepartmentName:
      deptVal != null ? payrollDepartments.find((d) => d.id === deptVal)?.name ?? null : null,
    mainLocationId: locVal,
    mainLocationName: locVal != null ? locations.find((l) => l.id === locVal)?.name ?? null : null,
    scheduleId: scheduleSingle,
    scheduleName:
      scheduleSingle != null ? schedules.find((s) => s.id === scheduleSingle)?.name ?? null : null,
    positionId: positionVal,
    positionName: positionVal != null ? positions.find((p) => p.id === positionVal)?.title ?? null : null,
    employeeIds,
    paymentFrequencyId,
    paymentFrequencyName:
      paymentFrequencyId != null
        ? paymentFrequencies.find((f) => f.id === paymentFrequencyId)?.name ?? null
        : null,
    // Hourly 29–28 → hourly staff; Salaried Monthly → salaried staff.
    payCategoryFilter:
      freqKind === 'ooh-hourly' ? 'hourly' : freqKind === 'monthly' ? 'salaried' : 'both',
  };
}

/** Further filter payroll preview rows using employee advanced filters (frontend-only). */
export function filterPayrollPreviewEmployees(params: {
  preview: PayrollRunPreviewDto;
  employeeFilters: FilterCondition[];
  filterFields: FilterFieldOption[];
  allEmployees: Employee[];
  allPayRecords?: PayRecord[];
}): EmployeePayrollCalculationResultDto[] {
  const { preview, employeeFilters, filterFields, allEmployees, allPayRecords = [] } = params;
  const allowedIds = new Set(
    filterEmployeesWithAdvancedFilters({
      allEmployees,
      selectedLocationId: null,
      statusFilter: getStatusFilterFromFilters(employeeFilters),
      filters: employeeFilters,
      searchTerm: '',
      filterFields,
      sortColumn: 'name',
      sortDirection: 'asc',
      allPayRecords,
      allLicenses: [],
      allFeedbackRecords: [],
      allTrainingRecords: [],
      allEmployeeNotes: [],
    }).map((e) => e.id)
  );

  return preview.employees.filter((emp) => allowedIds.has(emp.employeeId));
}

export function defaultPayrollBuilderReportFilters(runId: number | null): PayrollReportFilters {
  return {
    ...defaultPayrollFilters(),
    payrollRunId: runId,
  };
}

export function normalizePayrollBuilderLayout(v: string | undefined): PayrollReportLayout {
  return v === 'summary' ? 'summary' : 'detail';
}

export const RUN_HISTORY_PAGE_SIZE = 10;

export function filterPayrollRunsForSearch<
  T extends {
    id: number;
    periodStart: string;
    periodEnd: string;
    status: string;
    notes?: string | null;
    previewFiltersJson?: string | null;
  }
>(runs: T[], search: string): T[] {
  const q = search.trim().toLowerCase();
  if (!q) return runs;
  return runs.filter((r) => {
    let staffing = '';
    let cycle = '';
    try {
      if (r.previewFiltersJson) {
        const snap = JSON.parse(r.previewFiltersJson) as {
          freqKind?: string;
          payCategoryFilter?: string;
        };
        if (snap.payCategoryFilter === 'hourly' || snap.freqKind === 'ooh-hourly') staffing = 'hourly';
        else if (snap.payCategoryFilter === 'salaried' || snap.freqKind === 'monthly') staffing = 'salaried';
        else if (snap.payCategoryFilter === 'both') staffing = 'hourly salaried';
        cycle = snap.freqKind ?? '';
      }
    } catch {
      /* ignore */
    }
    const haystack = [
      String(r.id),
      r.periodStart?.slice(0, 10) ?? '',
      r.periodEnd?.slice(0, 10) ?? '',
      r.status ?? '',
      r.notes ?? '',
      staffing,
      cycle,
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  });
}
